import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { formatMinutes, formatTimeInTz, formatDateInTz } from "@/lib/timeFormat";
import ExportCsvButton from "@/components/ExportCsvButton";
import { AITimesheetTriggerButton } from "@/components/pace/AITimesheetModal";
import { usePaceStatus } from "@/hooks/use-pace-status";
import { useAuth } from "@/lib/auth";
import StatusPill from "@/components/hours/StatusPill";
import StatusFilter from "@/components/hours/StatusFilter";
import HoursSummaryStrip from "@/components/hours/HoursSummaryStrip";
import { AdminTimeEntryDialog } from "@/components/admin/AdminTimeEntryDialog";

export default function AdminTimePage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [filters, setFilters] = useState({ user_id: "all", worksite_id: "all", from: "", to: "", timesheet_status: null });
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [newEntryOpen, setNewEntryOpen] = useState(false);
  const { paceEnabled } = usePaceStatus();
  const { hasRole } = useAuth();
  const canSupervise = hasRole(["admin", "tenant_admin", "master"]);

  useEffect(() => {
    (async () => {
      const [u, w, t] = await Promise.all([
        api.get("/tenant/users"),
        api.get("/tenant/worksites"),
        api.get("/tenant/me"),
      ]);
      setUsers(u.data.items);
      setWorksites(w.data.items);
      setTenant(t.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (filters.user_id !== "all") params.user_id = filters.user_id;
      if (filters.worksite_id !== "all") params.worksite_id = filters.worksite_id;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.timesheet_status) params.status = filters.timesheet_status;
      const { data } = await api.get("/tenant/time/entries", { params });
      setItems(data.items);
      setLoading(false);
    })();
  }, [filters, reloadTick]);

  return (
    <div className="space-y-6" data-testid="admin-time-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Administration</div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Time entries</h1>
          <p className="mt-2 text-muted-foreground">All punches across this workspace. Read-only for now — approval flow lands in Phase 3.</p>
        </div>
        {paceEnabled && canSupervise && (
          <div data-testid="admin-time-ai-timesheet-slot">
            <AITimesheetTriggerButton
              workers={users}
              defaultWorkerId={filters.user_id !== "all" ? filters.user_id : ""}
              onCommitted={() => setReloadTick((n) => n + 1)}
            />
          </div>
        )}
        {canSupervise && (
          <Button
            onClick={() => setNewEntryOpen(true)}
            data-testid="admin-new-entry-btn"
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" /> New entry
          </Button>
        )}
      </div>

      <Card>
        <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
          <div className="space-y-2">
            <Label>Worker</Label>
            <Select value={filters.user_id} onValueChange={(v) => setFilters((f) => ({ ...f, user_id: v }))}>
              <SelectTrigger data-testid="filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Worksite</Label>
            <Select value={filters.worksite_id} onValueChange={(v) => setFilters((f) => ({ ...f, worksite_id: v }))}>
              <SelectTrigger data-testid="filter-ws"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                {worksites.map((w) => <SelectItem key={w.id} value={w.id}>{w.name} ({w.code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>From</Label>
            <Input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} data-testid="filter-from" />
          </div>
          <div className="space-y-2">
            <Label>To</Label>
            <Input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} data-testid="filter-to" />
          </div>
          </div>
          <div className="flex items-center gap-3">
            <Label className="mb-0">Timesheet status</Label>
            <StatusFilter
              value={filters.timesheet_status}
              onChange={(v) => setFilters((f) => ({ ...f, timesheet_status: v }))}
              persistKey="hours-filter:admin-time"
            />
          </div>
        </CardContent>
      </Card>

      {(filters.from || filters.to || filters.user_id !== "all") && (
        <HoursSummaryStrip
          workerId={filters.user_id !== "all" ? filters.user_id : undefined}
          from={filters.from || undefined}
          to={filters.to || undefined}
          variant="admin"
        />
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Entries</CardTitle>
          <ExportCsvButton
            endpoint="tenant/timesheets/export.csv"
            filename={`quanta-timesheets-${filters.from || "all"}.csv`}
            filters={{
              status: "approved",
              period_start: filters.from || undefined,
              period_end: filters.to || undefined,
              worksite_id: filters.worksite_id !== "all" ? filters.worksite_id : undefined,
              worker_id: filters.user_id !== "all" ? filters.user_id : undefined,
            }}
            testId="export-csv-admin"
          />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Worker</TableHead>
                <TableHead>Worksite</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>In</TableHead>
                <TableHead>Out</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Punch</TableHead>
                <TableHead>Sheet</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>}
              {!loading && items.length === 0 && <TableRow><TableCell colSpan={8} className="py-8 text-center text-muted-foreground">No entries match.</TableCell></TableRow>}
              {items.map((e) => (
                <TableRow key={e.id} data-testid={`admin-entry-${e.id}`}>
                  <TableCell className="font-medium">{e.user_full_name || e.user_email}</TableCell>
                  <TableCell>
                    {e.worksite_code ? (
                      <>
                        <Badge variant="secondary" className="rounded-full">{e.worksite_code}</Badge>
                        {" "}{e.worksite_name}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {e.entry_mode === "duration" && e.work_date
                      ? e.work_date
                      : (e.clock_in_at ? formatDateInTz(e.clock_in_at, tenant?.timezone) : <span className="text-muted-foreground">—</span>)}
                  </TableCell>
                  <TableCell>{e.clock_in_at ? formatTimeInTz(e.clock_in_at, tenant?.timezone) : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>{e.clock_out_at ? formatTimeInTz(e.clock_out_at, tenant?.timezone) : <span className="text-secondary">{e.entry_mode === "duration" ? <span className="text-muted-foreground">—</span> : "open"}</span>}</TableCell>
                  <TableCell>{formatMinutes(e.duration_minutes)}</TableCell>
                  <TableCell>
                    <Badge variant={e.status === "open" ? "outline" : "default"} className="capitalize">
                      {e.entry_mode === "duration" ? "duration" : e.status}
                    </Badge>
                  </TableCell>
                  <TableCell><StatusPill status={e.timesheet_status || "draft"} size="sm" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AdminTimeEntryDialog
        open={newEntryOpen}
        onOpenChange={setNewEntryOpen}
        endpoint="/admin/time-entries"
        workers={users}
        worksites={worksites}
        defaultWorkerId={filters.user_id !== "all" ? filters.user_id : ""}
        onCreated={() => setReloadTick((n) => n + 1)}
      />
    </div>
  );
}
